import Header from "../components/Header";
import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from 'chart.js';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MuiTooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import { useDashboardData } from "./hooks/useDashboardData";
import {
    CORES, KpiCard, Painel, StatusTag, LinhaProporcao, opcoesBarra, datasetBarra,
} from "./components/DashboardUI";
import { formatToBrl } from "../../utils/FormatToBrl";
import { FormatDate } from "../../utils/FormatDate";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const pct = (v) => `${(v * 100).toFixed(1).replace('.', ',')}%`;
const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

function variacao(atual, anterior) {
    if (!anterior) return atual ? { texto: 'sem base no mês anterior', tom: null } : null;
    const d = (atual - anterior) / anterior;
    const seta = d > 0 ? '▲' : d < 0 ? '▼' : '■';
    return { texto: `${seta} ${pct(Math.abs(d))} vs. mês anterior`, tom: d > 0 ? 'good' : d < 0 ? 'critical' : null };
}

function quandoCobra(dias) {
    if (dias < 0) return `${plural(-dias, 'dia', 'dias')} em atraso`;
    if (dias === 0) return 'hoje';
    if (dias === 1) return 'amanhã';
    return `em ${dias} dias`;
}

function LinhaCobranca({ a, onClick }) {
    const atrasada = a.diasAte < 0;
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                py: 1.25, px: 1, borderRadius: 2, cursor: 'pointer',
                '&:hover': { background: '#eef3f8' },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: CORES.tinta }} noWrap>
                    {a.usuario?.Nome || `Assinatura #${a.idAssinatura}`}
                </Typography>
                <Typography variant="caption" sx={{ color: CORES.tintaFraca }}>
                    {a.nomePlano || '—'} · {FormatDate(a.proximaCobranca)}
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {a.valor ? formatToBrl(a.valor) : '—'}
                </Typography>
                <StatusTag tom={atrasada ? 'critical' : a.diasAte <= 1 ? 'warning' : 'neutral'}>
                    {quandoCobra(a.diasAte)}
                </StatusTag>
            </Box>
        </Box>
    );
}

const Dashboard = () => {
    const navigate = useNavigate();
    const { metricas: m, erros, loading, atualizadoEm, recarregar } = useDashboardData();
    const carregando = loading && !m;

    const novasVar = m && variacao(m.novasMesAtual, m.novasMesAnterior);
    const erroAssinaturas = erros.assinaturas;

    return (
        <div style={{ background: '#f6f7f9', minHeight: '100vh' }}>
            <Header />
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>

                {/* Cabeçalho */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: CORES.marca }}>Visão geral</Typography>
                        <Typography variant="body2" sx={{ color: CORES.tintaFraca }}>
                            {atualizadoEm
                                ? `Atualizado às ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                : 'Carregando dados…'}
                        </Typography>
                    </Box>
                    <MuiTooltip title="Atualizar dados">
                        <span>
                            <IconButton onClick={recarregar} disabled={loading} sx={{ border: `1px solid ${CORES.grade}`, background: '#fff' }}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                </Box>

                {/* KPIs */}
                <Box sx={{
                    display: 'grid', gap: 2, mb: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' },
                }}>
                    <KpiCard
                        titulo="Receita mensal estimada"
                        valor={m && formatToBrl(m.receitaMensal)}
                        detalhe="Valor mensal dos planos das assinaturas ativas"
                        loading={carregando}
                        erro={erroAssinaturas}
                    />
                    <KpiCard
                        titulo="Assinaturas ativas"
                        valor={m?.ativas}
                        detalhe={m && `de ${m.totalAssinaturas} no total`}
                        loading={carregando}
                        erro={erroAssinaturas}
                        onClick={() => navigate("../Assinaturas")}
                    />
                    <KpiCard
                        titulo="Ticket médio"
                        valor={m && formatToBrl(m.ticketMedio)}
                        detalhe="Por assinatura ativa / mês"
                        loading={carregando}
                        erro={erroAssinaturas}
                    />
                    <KpiCard
                        titulo="Novas assinaturas no mês"
                        valor={m?.novasMesAtual}
                        detalhe={novasVar?.texto}
                        tomDetalhe={novasVar?.tom}
                        loading={carregando}
                        erro={erroAssinaturas}
                    />
                    <KpiCard
                        titulo="Usuários cadastrados"
                        valor={m?.totalUsuarios}
                        detalhe={m?.conversao != null ? `${pct(m.conversao)} são assinantes ativos` : undefined}
                        loading={carregando}
                        erro={erros.usuarios}
                        onClick={() => navigate("../Usuarios")}
                    />
                    <KpiCard
                        titulo="Leads recuperáveis"
                        valor={m?.totalLeads}
                        detalhe="Cadastros não finalizados"
                        tomDetalhe={m?.totalLeads ? 'warning' : null}
                        loading={carregando}
                        erro={erros.leads}
                        onClick={() => navigate("../Dashboard/Leads")}
                    />
                </Box>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' } }}>

                    {/* Crescimento */}
                    <Box sx={{ gridColumn: { md: 'span 8' } }}>
                        <Painel
                            titulo="Novas assinaturas por mês"
                            subtitulo="Últimos 12 meses, pela data de início"
                            loading={carregando}
                            erro={erroAssinaturas}
                            vazio={m && m.novasPorMes.every(x => x.total === 0)}
                            altura={260}
                        >
                            {m && (
                                <Bar
                                    data={{
                                        labels: m.novasPorMes.map(x => x.label),
                                        datasets: [datasetBarra('Novas assinaturas', m.novasPorMes.map(x => x.total))],
                                    }}
                                    options={opcoesBarra({ formatarValor: (v) => plural(v, 'nova assinatura', 'novas assinaturas') })}
                                />
                            )}
                        </Painel>
                    </Box>

                    {/* Status */}
                    <Box sx={{ gridColumn: { md: 'span 4' } }}>
                        <Painel
                            titulo="Status das assinaturas"
                            subtitulo={m && plural(m.totalAssinaturas, 'assinatura', 'assinaturas')}
                            acao="Ver todas"
                            onAcao={() => navigate("../Assinaturas")}
                            loading={carregando}
                            erro={erroAssinaturas}
                            vazio={m && m.totalAssinaturas === 0}
                            mensagemVazio="Nenhuma assinatura cadastrada."
                        >
                            {m?.porStatus.map(s => (
                                <LinhaProporcao key={s.status} label={s.label} valor={s.total} total={m.totalAssinaturas} tom={s.tom} />
                            ))}
                        </Painel>
                    </Box>

                    {/* Planos */}
                    <Box sx={{ gridColumn: { md: 'span 7' } }}>
                        <Painel
                            titulo="Assinaturas ativas por plano"
                            subtitulo="Passe o mouse para ver a receita mensal de cada plano"
                            acao="Gerenciar planos"
                            onAcao={() => navigate("../Planos")}
                            loading={carregando}
                            erro={erroAssinaturas}
                            vazio={m && m.porPlano.length === 0}
                            altura={240}
                        >
                            {m && (
                                <Bar
                                    data={{
                                        labels: m.porPlano.map(p => p.nome),
                                        datasets: [datasetBarra('Assinaturas ativas', m.porPlano.map(p => p.ativas))],
                                    }}
                                    options={opcoesBarra({
                                        horizontal: true,
                                        formatarValor: (v, ctx) => [
                                            plural(v, 'assinatura ativa', 'assinaturas ativas'),
                                            `${formatToBrl(m.porPlano[ctx.dataIndex].receita)} / mês`,
                                        ],
                                    })}
                                />
                            )}
                        </Painel>
                    </Box>

                    {/* Cobranças que pedem atenção */}
                    <Box sx={{ gridColumn: { md: 'span 5' } }}>
                        <Painel
                            titulo="Cobranças"
                            subtitulo="Assinaturas ativas com cobrança vencida ou nos próximos 7 dias"
                            loading={carregando}
                            erro={erroAssinaturas}
                            vazio={m && m.cobrancasAtrasadas.length === 0 && m.proximasCobrancas.length === 0}
                            mensagemVazio="Nenhuma cobrança vencida ou próxima."
                        >
                            {m && (
                                <Box sx={{ maxHeight: 260, overflowY: 'auto', mx: -1 }}>
                                    {m.cobrancasAtrasadas.length > 0 && (
                                        <>
                                            <Typography variant="caption" sx={{ px: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: '#c62828', fontWeight: 600 }}>
                                                <WarningAmberIcon sx={{ fontSize: 16 }} /> Vencidas ({m.cobrancasAtrasadas.length})
                                            </Typography>
                                            {m.cobrancasAtrasadas.map(a => (
                                                <LinhaCobranca key={a.idAssinatura} a={a} onClick={() => navigate("../Assinaturas")} />
                                            ))}
                                            {m.proximasCobrancas.length > 0 && <Divider sx={{ my: 1 }} />}
                                        </>
                                    )}
                                    {m.proximasCobrancas.length > 0 && (
                                        <>
                                            <Typography variant="caption" sx={{ px: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: CORES.tintaSecundaria, fontWeight: 600 }}>
                                                <EventIcon sx={{ fontSize: 16 }} /> Próximos 7 dias ({m.proximasCobrancas.length})
                                            </Typography>
                                            {m.proximasCobrancas.map(a => (
                                                <LinhaCobranca key={a.idAssinatura} a={a} onClick={() => navigate("../Assinaturas")} />
                                            ))}
                                        </>
                                    )}
                                </Box>
                            )}
                        </Painel>
                    </Box>

                    {/* Leads */}
                    <Box sx={{ gridColumn: { md: 'span 6' } }}>
                        <Painel
                            titulo="Onde os leads pararam"
                            subtitulo="Etapa do cadastro em que cada lead recuperável parou. Clique numa barra para ver a lista."
                            acao="Ver leads"
                            onAcao={() => navigate("../Dashboard/Leads")}
                            loading={carregando}
                            erro={erros.leads}
                            vazio={m && m.totalLeads === 0}
                            mensagemVazio="Nenhum lead recuperável no momento."
                            altura={260}
                        >
                            {m && (
                                <Bar
                                    data={{
                                        labels: m.leadsPorStep.map(s => `Etapa ${s.step}`),
                                        datasets: [datasetBarra('Leads', m.leadsPorStep.map(s => s.total))],
                                    }}
                                    options={opcoesBarra({
                                        formatarValor: (v) => `${plural(v, 'lead', 'leads')} (${pct(v / m.totalLeads)})`,
                                        onClick: (e, els) => { if (els.length) navigate("../Dashboard/Leads"); },
                                    })}
                                />
                            )}
                        </Painel>
                    </Box>

                    {/* Cancelamentos */}
                    <Box sx={{ gridColumn: { md: 'span 6' } }}>
                        <Painel
                            titulo="Motivos de cancelamento"
                            subtitulo={m && `${plural(m.totalCancelamentos, 'cancelamento', 'cancelamentos')} · um cliente pode marcar mais de um motivo`}
                            loading={carregando}
                            erro={erros.cancelamentos}
                            vazio={m && m.motivos.length === 0}
                            mensagemVazio="Nenhum cancelamento registrado."
                            altura={260}
                        >
                            {m && (
                                <Bar
                                    data={{
                                        labels: m.motivos.map(x => x.motivo),
                                        datasets: [datasetBarra('Clientes', m.motivos.map(x => x.total))],
                                    }}
                                    options={opcoesBarra({
                                        horizontal: true,
                                        formatarValor: (v) => `${plural(v, 'cliente', 'clientes')} (${pct(v / m.totalCancelamentos)} dos cancelamentos)`,
                                    })}
                                />
                            )}
                        </Painel>
                    </Box>

                    {/* Cupons */}
                    <Box sx={{ gridColumn: { md: 'span 12' } }}>
                        <Painel
                            titulo="Cupons de desconto"
                            subtitulo={m && `${m.cuponsAtivos} ativos de ${m.cupons.length} · ${plural(m.totalUsosCupons, 'uso', 'usos')} no total`}
                            acao="Gerenciar cupons"
                            onAcao={() => navigate("../Descontos")}
                            loading={carregando}
                            erro={erros.descontos}
                            vazio={m && m.cupons.length === 0}
                            mensagemVazio="Nenhum cupom cadastrado."
                        >
                            {m && (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Box component="table" sx={{
                                        width: '100%', borderCollapse: 'collapse', fontSize: 14,
                                        '& th': { textAlign: 'left', fontWeight: 600, color: CORES.tintaSecundaria, fontSize: 12, py: 1, px: 1.5, borderBottom: `1px solid ${CORES.grade}` },
                                        '& td': { py: 1.25, px: 1.5, borderBottom: `1px solid #f0efec`, fontVariantNumeric: 'tabular-nums' },
                                        '& tbody tr': { cursor: 'pointer' },
                                        '& tbody tr:hover': { background: '#eef3f8' },
                                    }}>
                                        <thead>
                                            <tr>
                                                <th>Código</th>
                                                <th>Desconto</th>
                                                <th>Duração</th>
                                                <th>Status</th>
                                                <th style={{ width: '35%' }}>Usos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {m.cupons.map(c => {
                                                const maxUsos = m.cupons[0]?.usos || 1;
                                                return (
                                                    <tr key={c.idDesconto} onClick={() => navigate(`../DescontosUsados/${c.idDesconto}`)}>
                                                        <td style={{ fontWeight: 600 }}>{c.discountCode}</td>
                                                        <td>{c.valorDesconto}%</td>
                                                        <td>{plural(Number(c.duracaoMeses), 'mês', 'meses')}</td>
                                                        <td><StatusTag tom={c.status ? 'good' : 'neutral'}>{c.status ? 'Ativo' : 'Inativo'}</StatusTag></td>
                                                        <td>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Box sx={{ flex: 1, height: 8, borderRadius: 4, background: '#f0efec', overflow: 'hidden' }}>
                                                                    <Box sx={{ width: `${(c.usos / maxUsos) * 100}%`, height: '100%', borderRadius: 4, background: CORES.serie }} />
                                                                </Box>
                                                                <Box component="span" sx={{ minWidth: 28, textAlign: 'right', fontWeight: 600 }}>{c.usos}</Box>
                                                            </Box>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Box>
                                </Box>
                            )}
                        </Painel>
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default Dashboard;
